import type { Channel } from "@auradata/contracts";
import { Chip, Picker, type PickerItem } from "@auradata/ui";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";
import { channelsQuery } from "@/lib/api/queries";

const glyph: Record<Channel["kind"], { icon: string; bg: string; fg: string; group: string }> = {
  slack: { icon: "S", bg: "#4a154b", fg: "#fff", group: "Slack" },
  pagerduty: { icon: "PD", bg: "#06ac38", fg: "#fff", group: "PagerDuty" },
  email: { icon: "@", bg: "#f0f2f5", fg: "#3d4654", group: "Email" },
  github: { icon: "GH", bg: "#0e1116", fg: "#fff", group: "GitHub" },
  webhook: { icon: "{}", bg: "#f0f2f5", fg: "#3d4654", group: "Webhook" },
  teams: { icon: "T", bg: "#5059c9", fg: "#fff", group: "Teams" },
};

export function channelItems(channels: Channel[]): PickerItem[] {
  return channels.map((c) => ({
    id: c.id,
    label: c.name,
    meta: c.target,
    group: glyph[c.kind].group,
    icon: glyph[c.kind].icon,
    iconBg: glyph[c.kind].bg,
    iconFg: glyph[c.kind].fg,
  }));
}

/** Multi-select over the workspace's channels with removable pills below. */
export function ChannelPicker({
  workspace,
  value,
  onChange,
  placeholder = "Add channels…",
}: {
  workspace: string;
  value: string[];
  onChange: (ids: string[]) => void;
  placeholder?: string;
}) {
  const { data: channels = [] } = useQuery(channelsQuery(workspace));
  const items = useMemo(() => channelItems(channels), [channels]);
  const navigate = useNavigate();
  return (
    <div className="flex flex-col gap-2">
      <Picker
        multiple
        items={items}
        value={value}
        onValueChange={onChange}
        placeholder={placeholder}
        searchPlaceholder={`Search ${channels.length} channels…`}
        mono={false}
        aria-label="Notification channels"
        footer={{
          label: "Manage channels in the workspace",
          onSelect: () =>
            navigate({ to: "/w/$workspace/$section", params: { workspace, section: "channels" } }),
        }}
      />
      {value.length ? (
        <div className="flex flex-wrap gap-1.5">
          {value.map((id) => {
            const c = channels.find((x) => x.id === id);
            return (
              <Chip key={id} onRemove={() => onChange(value.filter((v) => v !== id))}>
                {c?.name ?? id}
              </Chip>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
